<template>
  <section class="dash" aria-label="Resumen del taller">
    <div class="dash-content">
      <h1 class="sr-only">Dashboard del taller</h1>
      <section class="dashboard-block first-block" aria-label="Estado actual de los vehículos">
        <div v-if="cargandoAhora" class="stage-grid" aria-busy="true" aria-label="Cargando estados">
          <div v-for="n in 3" :key="'stage-' + n" class="skel stage-skeleton"></div>
        </div>
        <div v-else-if="errorAhora" class="inline-error" role="alert">
          <span>No pudimos cargar el estado de los vehículos.</span>
          <button type="button" @click="cargarAhora">Reintentar</button>
        </div>
        <div v-else class="stage-grid">
          <article v-for="item in taller" :key="item.label" class="stage-card" :class="item.tone">
            <div class="stage-icon"><AppIcon :name="item.icon" size="h-5 w-5" /></div>
            <div class="stage-copy">
              <p class="stage-value">{{ conteo(item.value) }}</p>
              <p class="stage-label">{{ item.label }}</p>
            </div>
          </article>
        </div>
      </section>

      <section class="dashboard-block" aria-labelledby="rendimiento-title">
        <div class="section-heading performance-heading">
          <div class="results-heading">
            <h2 id="rendimiento-title">Resultados</h2>
            <span class="range-label">{{ rango }}</span>
          </div>
          <div class="range-control">
            <div class="period-switch" role="group" aria-label="Periodo de resultados">
              <button
                v-for="item in periodos"
                :key="item.tipo"
                type="button"
                :aria-pressed="filtro === item.tipo ? 'true' : 'false'"
                :disabled="cargandoPeriodo"
                :class="{ selected: filtro === item.tipo }"
                @click="cambiarPeriodo(item.tipo)"
              >{{ item.label }}</button>
              <button
                type="button"
                :aria-pressed="filtro === 'custom' ? 'true' : 'false'"
                :aria-expanded="fechasAbiertas ? 'true' : 'false'"
                :class="{ selected: filtro === 'custom' }"
                @click="fechasAbiertas = !fechasAbiertas"
              >Fechas</button>
            </div>
            <RangeCalendar
              :open="fechasAbiertas"
              :inicio="fechaInicio"
              :fin="fechaFin"
              @pick="elegirRango"
              @apply="aplicarRango"
            />
          </div>
        </div>

        <div v-if="cargandoPeriodo" class="kpi-grid" aria-busy="true" aria-label="Cargando resultados">
          <div v-for="n in 4" :key="'kpi-' + n" class="skel kpi-skeleton"></div>
        </div>
        <div v-else-if="errorPeriodo" class="inline-error" role="alert">
          <span>No pudimos cargar los resultados de este periodo.</span>
          <button type="button" @click="cargarPeriodo">Reintentar</button>
        </div>
        <div v-else class="kpi-grid">
          <article class="kpi-card kpi-feature">
            <div class="kpi-top"><span>Cobrado</span><AppIcon name="banknotes" size="h-5 w-5" /></div>
            <p class="kpi-value">{{ dinero(periodo.ventas) }}</p>
            <p v-if="textoMom" class="kpi-comparison" :class="tonoMom" :title="detalleMom" :aria-label="detalleMom">
              <AppIcon :name="iconoMom" size="h-4 w-4" />
              <span>{{ textoMom }}</span>
            </p>
          </article>
          <article class="kpi-card">
            <div class="kpi-top"><span>Ticket promedio por orden</span><AppIcon name="receipt" size="h-5 w-5" /></div>
            <p class="kpi-value">{{ dinero(ticket) }}</p>
          </article>
          <article class="kpi-card">
            <div class="kpi-top"><span>Clientes nuevos</span><AppIcon name="user" size="h-5 w-5" /></div>
            <p class="kpi-value">{{ conteo(periodo.nuevos) }}</p>
          </article>
          <article class="kpi-card">
            <div class="kpi-top"><span>Órdenes nuevas</span><AppIcon name="clipboard" size="h-5 w-5" /></div>
            <p class="kpi-value">{{ conteo(periodo.ordenes) }}</p>
          </article>
        </div>
      </section>

      <section v-if="!cargandoPeriodo && !errorPeriodo" class="dashboard-block trend-block" aria-label="Tendencia anual">
        <div class="chart-grid">
          <YearChart :titulo="'Cobrado por mes · ' + anio" icono="banknotes" color="#0A4F9C" :meses="periodo.meses" compact />
          <YearChart :titulo="'Autos ingresados por mes · ' + anio" icono="truck" color="#0D766E" :dinero="false" :meses="periodo.entradas" compact />
        </div>
      </section>

    </div>
  </section>
</template>

<script>
import AppIcon from '@/components/ui/AppIcon.vue'
import RangeCalendar from '@/components/dashboard/RangeCalendar.vue'
import YearChart from '@/components/dashboard/YearChart.vue'
import Servicio from '@/services/reports'
import { deltaMes, formatCount, formatMoney, formatRango, PERIODOS, periodRange, ticketDe } from '@/dashboard/mapDashboard'

const AHORA_VACIO = {
  recepcion: 0,
  proceso: 0,
  terminado: 0,
}

const PERIODO_VACIO = {
  ventas: 0,
  ordenes: 0,
  nuevos: 0,
  entradas: [],
  carros: [],
  meses: [],
  comparacion: null,
}

export default {
  name: 'inicio',
  components: { AppIcon, RangeCalendar, YearChart },
  data() {
    return {
      periodos: PERIODOS,
      filtro: 'month',
      fechaInicio: '',
      fechaFin: '',
      fechasAbiertas: false,
      anio: new Date().getFullYear(),
      cargandoAhora: true,
      errorAhora: false,
      cargandoPeriodo: true,
      errorPeriodo: false,
      ahora: AHORA_VACIO,
      periodo: PERIODO_VACIO,
    }
  },
  computed: {
    rango() {
      if (this.filtro === 'custom') return formatRango(this.fechaInicio, this.fechaFin)
      return periodRange(this.filtro)
    },
    taller() {
      return [
        { label: 'En recepción', value: this.ahora.recepcion, tone: 'stage-reception', icon: 'clipboard' },
        { label: 'En reparación', value: this.ahora.proceso, tone: 'stage-repair', icon: 'wrench' },
        { label: 'Listos para entregar', value: this.ahora.terminado, tone: 'stage-ready', icon: 'check' },
      ]
    },
    ticket() {
      return ticketDe(this.periodo.ventas, this.periodo.ordenes)
    },
    delta() {
      return deltaMes(this.periodo.comparacion)
    },
    tonoMom() {
      const delta = this.delta
      if (!delta || delta.diff === 0) return 'es-igual'
      return delta.diff > 0 ? 'es-subio' : 'es-bajo'
    },
    iconoMom() {
      const delta = this.delta
      if (!delta || delta.diff === 0) return 'minus'
      return delta.diff > 0 ? 'trending-up' : 'trending-down'
    },
    textoMom() {
      if (this.filtro !== 'month') return ''
      const comparacion = this.periodo.comparacion
      if (!comparacion) return ''
      const delta = deltaMes(comparacion)
      if (delta.diff === 0) return 'Igual que el periodo anterior'
      const verbo = delta.diff > 0 ? 'Subió' : 'Bajó'
      if (delta.pct == null) return `${verbo} vs periodo anterior`
      return `${verbo} ${Math.abs(Math.round(delta.pct))}% vs periodo anterior`
    },
    detalleMom() {
      if (!this.textoMom) return ''
      const comparacion = this.periodo.comparacion
      const delta = deltaMes(comparacion)
      const contra = formatRango(comparacion.prevInicio, comparacion.prevFin)
      if (delta.diff === 0) return `Igual que ${contra}`
      const verbo = delta.diff > 0 ? 'Subió' : 'Bajó'
      return `${verbo} ${formatMoney(Math.abs(delta.diff))} contra ${contra}`
    },

  },
  mounted() {
    this.cargarAhora()
    this.cargarPeriodo()
  },
  methods: {
    dinero: formatMoney,
    conteo: formatCount,
    cambiarPeriodo(tipo) {
      if (this.filtro === tipo || this.cargandoPeriodo) return
      this.filtro = tipo
      this.fechasAbiertas = false
      this.cargarPeriodo()
    },
    elegirRango(rango) {
      this.fechaInicio = rango.inicio
      this.fechaFin = rango.fin
    },
    aplicarRango() {
      if (!this.fechaInicio || !this.fechaFin) return
      this.filtro = 'custom'
      this.fechasAbiertas = false
      this.cargarPeriodo()
    },
    async cargarAhora() {
      this.cargandoAhora = true
      this.errorAhora = false
      try {
        const res = await Servicio.ahora()
        const body = res && res.data
        if (!body || body.recepcion == null) {
          this.errorAhora = true
        } else {
          this.ahora = body
        }
      } catch (e) {
        this.errorAhora = true
      } finally {
        this.cargandoAhora = false
      }
    },
    async cargarPeriodo() {
      this.cargandoPeriodo = true
      this.errorPeriodo = false
      const rango = this.filtro === 'custom'
        ? { inicio: this.fechaInicio, fin: this.fechaFin }
        : null
      try {
        const res = await Servicio.periodo(this.filtro, rango)
        const body = res && res.data
        if (!body || body.status === false) {
          this.periodo = PERIODO_VACIO
          this.errorPeriodo = true
        } else {
          this.periodo = body
        }
      } catch (e) {
        this.periodo = PERIODO_VACIO
        this.errorPeriodo = true
      } finally {
        this.cargandoPeriodo = false
      }
    },
  },
}
</script>

<style scoped>
.dash { min-height: calc(100dvh - 52px); padding: 28px clamp(16px, 2.5vw, 36px) 48px; background: #f3f5f8; color: #0b1b2e; font-family: Inter, Roboto, sans-serif; }
.dash-content { max-width: 1480px; margin: 0 auto; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
.period-switch button:focus-visible, .inline-error button:focus-visible { outline: 3px solid #7ab9f2; outline-offset: 3px; }
.dashboard-block { margin-top: 30px; }
.dashboard-block.first-block { margin-top: 0; }
.section-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 14px; }
.section-heading h2 { margin: 0; color: #0b1b2e; font-size: 21px; font-weight: 700; line-height: 1.25; letter-spacing: -.015em; }
.results-heading { display: flex; flex-wrap: wrap; align-items: baseline; gap: 3px 12px; }
.stage-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.stage-card { display: flex; min-height: 112px; align-items: center; gap: 17px; padding: 19px 22px; border: 1px solid #e4e9ef; border-radius: 18px; background: #fff; box-shadow: 0 4px 15px rgba(16, 42, 67, .035); }
.stage-reception { border-color: #f5e4c2; background: #fffaf1; }
.stage-repair { border-color: #cfe0f1; background: #f1f7fd; }
.stage-ready { border-color: #cae8dc; background: #f1faf5; }
.stage-icon { display: grid; width: 48px; height: 48px; flex: none; place-items: center; border-radius: 14px; }
.stage-reception .stage-icon { color: #9a5b00; background: #fff3dd; }
.stage-repair .stage-icon { color: #0a4f9c; background: #eaf2fb; }
.stage-ready .stage-icon { color: #047857; background: #e3f5ed; }
.stage-value { margin: 0; color: #102a43; font-size: 31px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }
.stage-label { margin: 8px 0 0; color: #526273; font-size: 14px; font-weight: 600; line-height: 1.25; }
.range-label { color: #607187; font-size: 13px; font-weight: 500; }
.range-control { position: relative; max-width: 100%; }
.period-switch { display: inline-flex; max-width: 100%; gap: 2px; overflow-x: auto; padding: 4px; border: 1px solid #e2e8ef; border-radius: 13px; background: #fff; box-shadow: 0 3px 12px rgba(16, 42, 67, .04); }
.period-switch button { min-width: 51px; min-height: 38px; flex: none; padding: 0 11px; border: 0; border-radius: 9px; background: transparent; color: #526273; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; transition: background .16s, color .16s; }
.period-switch button:hover:not(.selected) { background: #edf3f8; color: #0b1b2e; }
.period-switch button.selected { background: #12304f; color: #fff; box-shadow: 0 2px 5px rgba(16, 42, 67, .17); }
.period-switch button:disabled { cursor: wait; }
.kpi-grid { display: grid; grid-template-columns: 1.3fr 1.15fr .9fr .9fr; gap: 14px; }
.kpi-card { display: flex; min-height: 156px; flex-direction: column; padding: 19px 21px; border: 1px solid #e4e9ef; border-radius: 18px; background: #fff; box-shadow: 0 4px 15px rgba(16, 42, 67, .035); }
.kpi-feature { border-color: #123d65; background: linear-gradient(135deg, #102a43, #174a76); box-shadow: 0 12px 24px rgba(16, 42, 67, .14); }
.kpi-top { display: flex; align-items: center; justify-content: space-between; gap: 6px; color: #526273; font-size: 13px; font-weight: 600; line-height: 1.3; }
.kpi-top svg { flex: none; color: #758ca2; }
.kpi-feature .kpi-top, .kpi-feature .kpi-top svg { color: #c1dcf2; }
.kpi-value { margin: 19px 0 0; color: #102a43; font-size: clamp(24px, 2.2vw, 32px); font-weight: 700; line-height: 1; letter-spacing: -.025em; font-variant-numeric: tabular-nums; white-space: nowrap; }
.kpi-feature .kpi-value { color: #fff; font-size: clamp(27px, 2.5vw, 36px); }
.kpi-comparison { display: flex; align-items: flex-start; gap: 5px; margin: auto 0 0; font-size: 12px; font-weight: 600; line-height: 1.4; }
.kpi-comparison svg { flex: none; margin-top: 1px; }
.es-subio { color: #047857; }
.es-bajo { color: #b91c1c; }
.es-igual { color: #526273; }
.kpi-feature .es-subio { color: #97e2bc; }
.kpi-feature .es-bajo { color: #ffbdc1; }
.kpi-feature .es-igual { color: #c1dcf2; }
.chart-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.inline-error { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 80px; padding: 18px 20px; border: 1px solid #f2c6c9; border-radius: 16px; background: #fff8f8; color: #8d232d; font-size: 14px; }
.inline-error button { flex: none; padding: 9px 13px; border: 1px solid #b91c1c; border-radius: 8px; background: #fff; color: #a3212b; font: inherit; font-weight: 700; cursor: pointer; }
.skel { background: linear-gradient(100deg, #e6ebf1 20%, #f5f7fa 40%, #e6ebf1 60%); background-size: 200% 100%; animation: skel 1.4s ease-in-out infinite; }
.stage-skeleton { height: 112px; border-radius: 18px; }
.kpi-skeleton { height: 156px; border-radius: 18px; }
@keyframes skel { to { background-position-x: -200%; } }
@media (max-width: 1023px) { .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (min-width: 1024px) {
  .dash { padding-top: 16px; padding-bottom: 12px; }
  .dashboard-block { margin-top: 16px; }
  .dashboard-block.first-block { margin-top: 0; }
  .section-heading { margin-bottom: 8px; }
  .stage-card { min-height: 82px; padding: 10px 16px; gap: 12px; }
  .stage-icon { width: 42px; height: 42px; }
  .stage-value { font-size: 28px; }
  .stage-label { margin-top: 5px; }
  .kpi-card { min-height: 112px; padding: 12px 16px; }
  .kpi-value { margin-top: 14px; font-size: clamp(22px, 2vw, 30px); }
  .kpi-feature .kpi-value { font-size: clamp(25px, 2.25vw, 34px); }
  .trend-block { margin-top: 14px; }
}
@media (max-width: 780px) {
  .dash { padding: 18px 16px 32px; }
  .section-heading.performance-heading { align-items: flex-start; flex-direction: column; }
  .range-control, .period-switch { width: 100%; }
  .stage-grid { gap: 9px; }
  .stage-card { min-height: 100px; padding: 15px 12px; gap: 10px; }
  .stage-icon { width: 38px; height: 38px; border-radius: 11px; }
  .stage-value { font-size: 26px; }
  .chart-grid { grid-template-columns: 1fr; }
}
@media (max-width: 580px) {
  .dashboard-block { margin-top: 20px; }
  .section-heading h2 { font-size: 19px; }
  .stage-grid { grid-template-columns: 1fr; }
  .stage-card { min-height: 82px; padding: 15px 18px; }
  .kpi-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
  .kpi-card { min-height: 122px; padding: 14px; }
  .kpi-feature { grid-column: 1 / -1; }
  .kpi-card:last-child { grid-column: 1 / -1; min-height: 92px; }
  .kpi-value { font-size: 27px; }
  .kpi-feature .kpi-value { font-size: 34px; }
}
@media (max-width: 380px) { .kpi-grid { grid-template-columns: 1fr; } .kpi-feature, .kpi-card:last-child { grid-column: auto; } }
@media (prefers-reduced-motion: reduce) { .skel { animation: none; } .period-switch button { transition: none; } }
</style>
